import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "330",
  "slug": "how-do-time-zones-work",
  "title": "How Do Time Zones Work? The Geography Behind the Clock",
  "excerpt": "Why is it tomorrow in Tokyo when it's still today in New York? Here's how time zones map onto the planet, why some are offset by 30 or 45 minutes, and where a single day begins and ends.",
  "date": "2026-06-02",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1609609018625-afef0a259159?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxlYXJ0aCUyMGZyb20lMjBzcGFjZSUyMG5pZ2h0fGVufDF8MHx8fDE3ODAzOTMyNzZ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Earth turns 15° of longitude every hour, so the globe is divided into 24 standard time zones measured from the Prime Meridian (UTC). Political borders bend the lines, a handful of places use 30- or 45-minute offsets, and the International Date Line near 180° longitude is where each new calendar day begins.",
  "content": [
    {
      "type": "paragraph",
      "text": "Call a friend in Tokyo from New York and you might wake them up tomorrow — literally. Time zones are one of those things everyone uses and almost no one stops to explain. The whole system comes down to a single fact: the Earth is a spinning sphere, and the Sun can only be overhead in one place at a time."
    },
    {
      "type": "heading",
      "text": "15 Degrees an Hour: The Basic Idea"
    },
    {
      "type": "paragraph",
      "text": "The Earth makes one full turn — 360 degrees of longitude — every 24 hours. Divide 360 by 24 and you get 15: the planet rotates 15 degrees of longitude every hour. So the world is carved into 24 north–south slices, each roughly 15 degrees of longitude wide, and each one hour apart from its neighbours. Everything is measured from Coordinated Universal Time (UTC), the modern successor to Greenwich Mean Time, anchored at the Prime Meridian. As you move east you add hours; as you move west you subtract them."
    },
    {
      "type": "heading",
      "text": "A Brief History: Why We Have Them at All"
    },
    {
      "type": "paragraph",
      "text": "Standard time zones are surprisingly recent. For most of history, every town simply set its clocks by the local Sun, so noon in one city was a few minutes off from noon in the next. That was fine when the fastest thing around was a horse. The railways broke it: trains crossed dozens of local times in a single day, making timetables a nightmare and level crossings genuinely dangerous. In the 1870s and 1880s the Scottish-Canadian engineer Sandford Fleming championed a worldwide system of standard time zones, and in 1883 North American railroads adopted standard time. The following year, the 1884 International Meridian Conference set Greenwich as the global reference, and the modern system grew from there."
    },
    {
      "type": "heading",
      "text": "Why the Lines Aren't Straight"
    },
    {
      "type": "paragraph",
      "text": "If time zones were tidy 15-degree slices, a map of them would be a set of clean vertical stripes. In reality the boundaries zigzag all over the place, because countries set their own clocks and almost no one wants a time-zone border running through the middle of a city. So the lines bend to follow national and regional borders."
    },
    {
      "type": "paragraph",
      "text": "The most dramatic example is China. The country is physically wide enough to span about five geographic time zones, but the entire nation officially runs on a single time, UTC+8. That means sunrise in the far west of China can come hours 'late' by the clock. At the other extreme, France — counting all its overseas territories scattered around the globe — uses more distinct time zones than any other country."
    },
    {
      "type": "heading",
      "text": "The Half-Hour and Quarter-Hour Zones"
    },
    {
      "type": "paragraph",
      "text": "Not every offset is a whole number of hours. A handful of places have decided that a partial offset fits their geography better:"
    },
    {
      "type": "list",
      "items": [
        "India runs on UTC+5:30 — a single half-hour zone for the entire country.",
        "Nepal uses UTC+5:45, one of the only quarter-hour offsets in the world, deliberately set apart from neighbouring India.",
        "The Canadian province of Newfoundland sits at UTC-3:30, famous for its half-hour difference from the rest of the country.",
        "Parts of central Australia use UTC+9:30."
      ]
    },
    {
      "type": "heading",
      "text": "Where the Day Begins: The International Date Line"
    },
    {
      "type": "paragraph",
      "text": "If you keep adding hours as you travel east and subtracting as you go west, the two eventually have to meet — and where they meet, the date has to jump. That seam is the International Date Line, running roughly along 180° longitude in the middle of the Pacific. Cross it heading west and you skip forward a day; cross it heading east and you repeat one. The line isn't straight either: it kinks around island nations so that, for instance, all of Kiribati shares the same calendar day. The far eastern part of Kiribati, at UTC+14, is where each new day on Earth begins."
    },
    {
      "type": "heading",
      "text": "Daylight Saving Adds Another Layer"
    },
    {
      "type": "paragraph",
      "text": "As if borders weren't enough, many countries shift their clocks forward in summer and back in winter for daylight saving time — but not all of them, and not on the same dates. Because the Northern and Southern Hemispheres have opposite seasons, the time difference between two cities can change several times a year. It's why scheduling an international call is genuinely harder than it looks, and why your phone quietly does the maths for you."
    },
    {
      "type": "heading",
      "text": "UTC, GMT, and Zulu Time"
    },
    {
      "type": "paragraph",
      "text": "You'll see the reference time called several things. GMT (Greenwich Mean Time) is the historical term; UTC (Coordinated Universal Time) is the modern, atomic-clock-based standard that replaced it for technical use. Aviation and the military often call it 'Zulu time', after the phonetic alphabet letter for the zero zone. They're close enough to be used interchangeably in everyday life, which is why pilots, the crew of the International Space Station, and global IT systems all run on UTC — it sidesteps any argument about whose local clock counts as 'right'."
    }
  ]
};
